import type { AppNotification, NotificationData } from "./types";

/** Editor route for each docs `document_type` (services/docs DocumentTypeChoices). */
const EDITOR_BY_TYPE: Record<string, string> = {
  document: "/write",
  presentation: "/present",
  spreadsheet: "/grid",
};

/**
 * Shell path a notification opens (spec §5). The payload names a module
 * (`data.route`) plus whichever ids that module understands; this maps them
 * onto the shell's real deep-link routes — the same ones global search uses —
 * so a click lands on the conversation, thread, event or file, not just the
 * module's home.
 *
 * Only in-app paths are ever returned: `data` comes from other services and
 * must not be able to send the user to another origin.
 */
export function notificationHref(n: Pick<AppNotification, "kind" | "category" | "data">): string {
  const data: NotificationData = n.data ?? {};
  const route = safeRoute(data.route) ?? routeForCategory(n.category);
  const id = (key: keyof NotificationData): string | null => {
    const v = data[key];
    return typeof v === "string" && v.trim() ? encodeURIComponent(v.trim()) : null;
  };

  switch (route) {
    case "/chat": {
      const conversation = id("conversation_id");
      if (!conversation) return "/chat";
      const message = id("message_id");
      return message ? `/chat/c/${conversation}?focus=${message}` : `/chat/c/${conversation}`;
    }
    case "/email": {
      const thread = id("thread_id");
      return thread ? `/email/thread/${thread}` : "/email";
    }
    case "/planner": {
      // The planner's deep link needs the calendar as well as the event uid
      // (`/planner/event/:calendarId/:uid`, PlannerModule).
      const calendar = id("calendar_id");
      const event = id("event_id");
      return calendar && event ? `/planner/event/${calendar}/${event}` : "/planner";
    }
    case "/homework":
      return homeworkPath(n.kind);
    case "/vault":
      // Vault is the Files screen now (`/vault/*` still redirects there).
      return filesItemHref(id("item_id"), data.item_type);
    case "/files":
    case "/write":
    case "/present":
    case "/grid": {
      const doc = id("document_id");
      // Docs' own notifications say "/files" and name the document's kind:
      // a presentation or spreadsheet must open in its own editor, not Write.
      if (doc) {
        const editor = route === "/files" ? EDITOR_BY_TYPE[id("document_type") ?? ""] ?? "/write" : route;
        // A mention or comment opens the editor at that comment.
        const comment = id("comment_id");
        const thread = id("thread_id");
        const query = comment ? `?comment=${comment}${thread ? `&thread=${thread}` : ""}` : "";
        return `${editor}/${doc}${query}`;
      }
      const item = id("item_id");
      if (item) return filesItemHref(item, data.item_type);
      return route;
    }
    case "/call":
      // An incoming call is answered from the ringing banner; a click on the
      // row (or on a missed call) opens the call home, never straight into a
      // room — joining is the user's decision.
      return "/call";
    default:
      return route ?? "/";
  }
}

/** A Files item: a folder opens as the folder, a file opens its details panel. */
function filesItemHref(item: string | null, itemType: unknown): string {
  if (!item) return "/files";
  return itemType === "folder" ? `/files/folder/${item}` : `/files?file=${item}`;
}

function homeworkPath(kind: string): string {
  switch (kind) {
    case "office.announcement":
      return "/homework/announcements";
    case "office.leave_request":
    case "office.leave_decided":
      return "/homework/leave";
    default:
      // A knock is answered on the floor.
      return "/homework";
  }
}

function routeForCategory(category: string): string | null {
  switch (category) {
    case "chat":
      return "/chat";
    case "calls":
      return "/call";
    case "email":
      return "/email";
    case "homework":
      return "/homework";
    case "docs":
      return "/files";
    case "calendar":
      return "/planner";
    case "tasks":
      return "/tasks";
    default:
      return null;
  }
}

/** A same-origin absolute path with no query/hash, or null. */
function safeRoute(route: unknown): string | null {
  if (typeof route !== "string") return null;
  const r = route.trim();
  if (!r.startsWith("/") || r.startsWith("//") || r.includes("\\")) return null;
  const path = r.split(/[?#]/)[0].replace(/\/+$/, "");
  return path || "/";
}
